// Services Supabase pour le systeme de projets communautaires
import { supabase } from './supabaseClient';
import {
    ProjectCategory,
    CommunityProject,
    ProjectMilestone,
    ProjectParticipant,
    ProjectFunding,
    ProjectVote,
    ProjectComment
} from '../types/project.types';

// Re-export types for convenience
export type {
    ProjectCategory,
    CommunityProject,
    ProjectMilestone,
    ProjectParticipant,
    ProjectFunding,
    ProjectVote,
    ProjectComment
};

// Extended types for service responses
export interface CommunityProjectWithRelations extends CommunityProject {
    category?: ProjectCategory;
    milestones?: ProjectMilestone[];
    participants_count?: number;
}

export interface ProjectParticipantWithMember extends ProjectParticipant {
    member?: {
        first_name: string;
        last_name: string;
        selfie_url?: string;
    };
}

export interface ProjectCommentWithReplies extends ProjectComment {
    member?: {
        first_name: string;
        last_name: string;
        selfie_url?: string;
    };
    replies?: ProjectCommentWithReplies[];
}

// =====================================================
// SERVICES CATEGORIES
// =====================================================

export const projectCategoryService = {
    async getAll(): Promise<ProjectCategory[]> {
        const { data, error } = await supabase
            .from('project_categories')
            .select('*')
            .eq('is_active', true)
            .order('name');

        if (error) throw error;
        return data || [];
    },

    async getWithCount(): Promise<(ProjectCategory & { project_count: number })[]> {
        const { data: categories, error } = await supabase
            .from('project_categories')
            .select('*')
            .eq('is_active', true)
            .order('name');

        if (error) throw error;

        // Compter les projets par categorie
        const { data: counts } = await supabase
            .from('community_projects')
            .select('category_id')
            .in('status', ['approved', 'in_progress', 'fundraising', 'completed']);

        const countMap = (counts || []).reduce((acc, p) => {
            acc[p.category_id] = (acc[p.category_id] || 0) + 1;
            return acc;
        }, {} as Record<number, number>);

        return (categories || []).map(cat => ({
            ...cat,
            project_count: countMap[cat.id] || 0
        }));
    }
};

// =====================================================
// SERVICES PROJETS
// =====================================================

export const projectService = {
    // Recuperer les projets actifs (publics)
    async getActive(options?: {
        categoryId?: number;
        province?: string;
        status?: string;
        featured?: boolean;
        limit?: number;
        offset?: number;
        search?: string;
    }): Promise<{ projects: CommunityProjectWithRelations[]; total: number }> {
        let query = supabase
            .from('community_projects')
            .select(`
                *,
                category:project_categories(id, name, color, icon)
            `, { count: 'exact' })
            .eq('is_public', true)
            .in('status', ['approved', 'in_progress', 'fundraising']);

        if (options?.categoryId) {
            query = query.eq('category_id', options.categoryId);
        }

        if (options?.province) {
            query = query.eq('location_province', options.province);
        }

        if (options?.status) {
            query = query.eq('status', options.status);
        }

        if (options?.featured !== undefined) {
            query = query.eq('featured', options.featured);
        }

        if (options?.search) {
            query = query.or(`title.ilike.%${options.search}%,description.ilike.%${options.search}%,location_city.ilike.%${options.search}%`);
        }

        query = query.order('featured', { ascending: false })
            .order('created_at', { ascending: false });

        if (options?.limit) {
            query = query.limit(options.limit);
        }

        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
        }

        const { data, error, count } = await query;

        if (error) throw error;
        return { projects: data || [], total: count || 0 };
    },

    // Recuperer les projets completes
    async getCompleted(limit?: number): Promise<CommunityProjectWithRelations[]> {
        let query = supabase
            .from('community_projects')
            .select(`
                *,
                category:project_categories(id, name, color, icon)
            `)
            .eq('is_public', true)
            .eq('status', 'completed')
            .order('completion_percentage', { ascending: false })
            .order('updated_at', { ascending: false });

        if (limit) {
            query = query.limit(limit);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Recuperer les projets vedettes
    async getFeatured(limit: number = 4): Promise<CommunityProjectWithRelations[]> {
        const { data, error } = await supabase
            .from('community_projects')
            .select(`
                *,
                category:project_categories(id, name, color, icon)
            `)
            .eq('is_public', true)
            .eq('featured', true)
            .in('status', ['approved', 'in_progress', 'fundraising'])
            .order('budget_raised', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    },

    // Recuperer un projet par ID avec details
    async getById(id: number): Promise<CommunityProjectWithRelations | null> {
        const { data: project, error } = await supabase
            .from('community_projects')
            .select(`
                *,
                category:project_categories(id, name, color, icon)
            `)
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }

        // Recuperer les jalons
        const { data: milestones } = await supabase
            .from('project_milestones')
            .select('*')
            .eq('project_id', id)
            .order('order_index');

        // Compter les participants
        const { count: participants_count } = await supabase
            .from('project_participants')
            .select('id', { count: 'exact' })
            .eq('project_id', id)
            .in('status', ['confirmed', 'active']);

        return {
            ...project,
            milestones: milestones || [],
            participants_count: participants_count || 0
        };
    },

    // Recuperer les provinces avec projets
    async getProvinces(): Promise<string[]> {
        const { data, error } = await supabase
            .from('community_projects')
            .select('location_province')
            .eq('is_public', true)
            .not('location_province', 'is', null);

        if (error) throw error;

        const provinces = Array.from(new Set(data?.map(p => p.location_province).filter(Boolean) || []));
        return provinces.sort();
    }
};

// =====================================================
// SERVICES PARTICIPATION
// =====================================================

export const projectParticipationService = {
    // Rejoindre un projet
    async join(projectId: number, memberId: string, data: {
        role: string;
        skills_offered?: string[];
        time_commitment?: string;
        contribution_type: 'time' | 'skills' | 'funding' | 'resources' | 'promotion';
    }): Promise<ProjectParticipant> {
        const { data: participant, error } = await supabase
            .from('project_participants')
            .insert({
                project_id: projectId,
                member_id: memberId,
                ...data,
                status: 'interested'
            })
            .select()
            .single();

        if (error) throw error;
        return participant;
    },

    // Verifier si un membre participe deja
    async isParticipating(projectId: number, memberId: string): Promise<boolean> {
        const { count } = await supabase
            .from('project_participants')
            .select('id', { count: 'exact' })
            .eq('project_id', projectId)
            .eq('member_id', memberId)
            .not('status', 'eq', 'withdrawn');

        return (count || 0) > 0;
    },

    // Recuperer les participants d'un projet
    async getParticipants(projectId: number): Promise<ProjectParticipantWithMember[]> {
        const { data, error } = await supabase
            .from('project_participants')
            .select(`
                *,
                member:members(first_name, last_name, selfie_url)
            `)
            .eq('project_id', projectId)
            .in('status', ['confirmed', 'active'])
            .order('joined_at');

        if (error) throw error;
        return data || [];
    },

    // Recuperer les projets d'un membre
    async getMemberProjects(memberId: string): Promise<CommunityProjectWithRelations[]> {
        const { data, error } = await supabase
            .from('project_participants')
            .select(`
                project:community_projects(
                    *,
                    category:project_categories(id, name, color, icon)
                )
            `)
            .eq('member_id', memberId)
            .not('status', 'eq', 'withdrawn');

        if (error) throw error;
        return (data?.map(d => d.project).filter(Boolean) || []) as unknown as CommunityProjectWithRelations[];
    },

    // Se retirer d'un projet
    async withdraw(projectId: number, memberId: string): Promise<void> {
        const { error } = await supabase
            .from('project_participants')
            .update({
                status: 'withdrawn',
                left_at: new Date().toISOString()
            })
            .eq('project_id', projectId)
            .eq('member_id', memberId);

        if (error) throw error;
    }
};

// =====================================================
// SERVICES FINANCEMENT
// =====================================================

export const projectFundingService = {
    // Enregistrer une contribution
    async contribute(projectId: number, data: {
        contributorId?: string;
        contributorName?: string;
        amount: number;
        contributionType: 'donation' | 'investment' | 'grant' | 'sponsorship';
        paymentMethod: 'mobile_money' | 'bank_transfer' | 'cash' | 'crypto' | 'other';
        paymentReference?: string;
        isAnonymous?: boolean;
        message?: string;
    }): Promise<ProjectFunding> {
        const { data: funding, error } = await supabase
            .from('project_funding')
            .insert({
                project_id: projectId,
                contributor_id: data.contributorId || null,
                contributor_name: data.contributorName || null,
                amount: data.amount,
                contribution_type: data.contributionType,
                payment_method: data.paymentMethod,
                payment_reference: data.paymentReference || null,
                is_anonymous: data.isAnonymous || false,
                message: data.message || null,
                status: 'pending'
            })
            .select()
            .single();

        if (error) throw error;
        return funding;
    },

    // Recuperer les contributions d'un projet
    async getProjectFunding(projectId: number): Promise<ProjectFunding[]> {
        const { data, error } = await supabase
            .from('project_funding')
            .select('*')
            .eq('project_id', projectId)
            .eq('status', 'confirmed')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    },

    // Recuperer les contributions d'un membre
    async getMemberContributions(memberId: string): Promise<ProjectFunding[]> {
        const { data, error } = await supabase
            .from('project_funding')
            .select(`
                *,
                project:community_projects(id, title)
            `)
            .eq('contributor_id', memberId)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    }
};

// =====================================================
// SERVICES VOTES
// =====================================================

export const projectVoteService = {
    // Voter pour un projet
    async vote(projectId: number, memberId: string, voteType: 'support' | 'oppose' | 'neutral', comment?: string): Promise<void> {
        const { error } = await supabase
            .from('project_votes')
            .upsert({
                project_id: projectId,
                voter_id: memberId,
                vote_type: voteType,
                comment: comment || null
            }, {
                onConflict: 'project_id,voter_id'
            });

        if (error) throw error;
    },

    // Recuperer le vote d'un membre
    async getMemberVote(projectId: number, memberId: string): Promise<ProjectVote | null> {
        const { data, error } = await supabase
            .from('project_votes')
            .select('*')
            .eq('project_id', projectId)
            .eq('voter_id', memberId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }
        return data;
    },

    // Recuperer les stats de votes d'un projet
    async getVoteStats(projectId: number): Promise<{ support: number; oppose: number; neutral: number }> {
        const { data, error } = await supabase
            .from('project_votes')
            .select('vote_type')
            .eq('project_id', projectId);

        if (error) throw error;

        const stats = { support: 0, oppose: 0, neutral: 0 };
        data?.forEach(v => {
            stats[v.vote_type as keyof typeof stats]++;
        });
        return stats;
    }
};

// =====================================================
// SERVICES COMMENTAIRES
// =====================================================

export const projectCommentService = {
    // Recuperer les commentaires d'un projet
    async getByProject(projectId: number): Promise<ProjectCommentWithReplies[]> {
        const { data, error } = await supabase
            .from('project_comments')
            .select(`
                *,
                member:members(first_name, last_name, selfie_url)
            `)
            .eq('project_id', projectId)
            .eq('is_approved', true)
            .is('parent_comment_id', null)
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Recuperer les reponses
        if (data && data.length > 0) {
            const commentIds = data.map(c => c.id);
            const { data: replies } = await supabase
                .from('project_comments')
                .select(`
                    *,
                    member:members(first_name, last_name, selfie_url)
                `)
                .in('parent_comment_id', commentIds)
                .eq('is_approved', true)
                .order('created_at', { ascending: true });

            return data.map(comment => ({
                ...comment,
                replies: replies?.filter(r => r.parent_comment_id === comment.id) || []
            }));
        }

        return data || [];
    },

    // Ajouter un commentaire
    async add(projectId: number, memberId: string, comment: string, parentCommentId?: number): Promise<ProjectCommentWithReplies> {
        const { data, error } = await supabase
            .from('project_comments')
            .insert({
                project_id: projectId,
                commenter_id: memberId,
                comment,
                parent_comment_id: parentCommentId || null
            })
            .select(`
                *,
                member:members(first_name, last_name, selfie_url)
            `)
            .single();

        if (error) throw error;
        return data;
    }
};

// =====================================================
// STATISTIQUES
// =====================================================

export const projectStatsService = {
    // Statistiques globales
    async getGlobalStats(): Promise<{
        totalProjects: number;
        totalFundsRaised: number;
        totalParticipants: number;
        completedProjects: number;
    }> {
        const { count: totalProjects } = await supabase
            .from('community_projects')
            .select('id', { count: 'exact' })
            .eq('is_public', true)
            .in('status', ['approved', 'in_progress', 'fundraising', 'completed']);

        const { count: completedProjects } = await supabase
            .from('community_projects')
            .select('id', { count: 'exact' })
            .eq('status', 'completed');

        const { data: projects } = await supabase
            .from('community_projects')
            .select('budget_raised');

        const totalFundsRaised = projects?.reduce((sum, p) => sum + (p.budget_raised || 0), 0) || 0;

        const { count: totalParticipants } = await supabase
            .from('project_participants')
            .select('id', { count: 'exact' })
            .in('status', ['confirmed', 'active']);

        return {
            totalProjects: totalProjects || 0,
            totalFundsRaised,
            totalParticipants: totalParticipants || 0,
            completedProjects: completedProjects || 0
        };
    },

    // Statistiques pour un membre
    async getMemberStats(memberId: string): Promise<{
        projectsParticipating: number;
        totalContributed: number;
        projectsSupported: number;
    }> {
        const { count: projectsParticipating } = await supabase
            .from('project_participants')
            .select('id', { count: 'exact' })
            .eq('member_id', memberId)
            .not('status', 'eq', 'withdrawn');

        const { data: contributions } = await supabase
            .from('project_funding')
            .select('amount')
            .eq('contributor_id', memberId)
            .eq('status', 'confirmed');

        const totalContributed = contributions?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0;

        const { count: projectsSupported } = await supabase
            .from('project_votes')
            .select('id', { count: 'exact' })
            .eq('voter_id', memberId)
            .eq('vote_type', 'support');

        return {
            projectsParticipating: projectsParticipating || 0,
            totalContributed,
            projectsSupported: projectsSupported || 0
        };
    }
};
